/**
Author - Venkatesh Pullaganti😃
Git - https://github.com/venkatesh1146
**/

import React, {useEffect, useCallback} from 'react';
import PropTypes from 'prop-types';

import {AAProvider, useAAState} from './context/AAContext';
import {triggerToast} from 'helpers';
import useConsentPolling from './hooks/useConsentPolling';
import useAAActions from './hooks/useAAActions';
import useTabTracking from './hooks/useTabTracking';
import AccountAggregator from './AccountAggregator';

const propTypes = {
  updateStatus: PropTypes.func.isRequired,
  flow: PropTypes.string,
};

const AccountAggregatorContent = ({updateStatus, flow}) => {
  const state = useAAState();
  const {startPolling, stopPolling, pollingIntervalRef} = useConsentPolling(updateStatus);

  const handleTabClose = useCallback(async () => {
    stopPolling();
    triggerToast('Verification window closed. Checking status...');
    await updateStatus();
  }, [stopPolling, updateStatus]);

  const {openInNewTab, windowRef, trackingIntervalRef} = useTabTracking(handleTabClose);

  const {
    fetchCurrentConsentStatus,
    handleContinue,
    handleSkip,
    handleUploadIncome,
    handleRetry,
    isSkipLoading,
  } = useAAActions(updateStatus, startPolling, stopPolling, openInNewTab);

  useEffect(() => {
    fetchCurrentConsentStatus();
  }, [fetchCurrentConsentStatus]);

  useEffect(
    () => () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      if (trackingIntervalRef.current) {
        clearInterval(trackingIntervalRef.current);
      }
      if (windowRef.current && !windowRef.current.closed) {
        windowRef.current.close();
      }
    },
    [pollingIntervalRef, trackingIntervalRef, windowRef]
  );

  return (
    <AccountAggregator
      viewState={state.viewState}
      consent={state.consent}
      isLoading={state.isLoading || isSkipLoading}
      handleContinue={handleContinue}
      handleSkip={handleSkip}
      handleUploadIncome={handleUploadIncome}
      handleRetry={handleRetry}
      flow={flow}
    />
  );
};

AccountAggregatorContent.propTypes = propTypes;

const AccountAggregatorContainer = ({updateStatus, flow}) => (
  <AAProvider>
    <AccountAggregatorContent updateStatus={updateStatus} flow={flow} />
  </AAProvider>
);

AccountAggregatorContainer.propTypes = propTypes;

export default AccountAggregatorContainer;
